<template>
  <!-- Create Forum -->
  <b-modal
    id="modal-create-forums"
    :title="$t('form-create-item.create-forum')"
    cancel-variant="outline-secondary"
    :ok-title="isSubmitting ? $t('saving') : $t('form.actions.save')"
    :ok-disabled="isSubmitting"
    :cancel-title="$t('form-create-item.cancel')"
    :cancel-disabled="isSubmitting"
    centered
    size="lg"
    @ok="handleCreateForum"
    @hidden="resetForm"
  >
    <b-form>
      <b-form-group v-if="showCreateTitle">
        <label for="name">
           {{ $t("form-create-item.title") }}
        </label>
        <b-form-input
          id="name"
          v-model="form.name"
          :placeholder="$t('form.placeholder')"
          type="text"
        />
      </b-form-group>
      <b-form-group>
        <label
          class="mt-2"
          for="description"
        >
          {{ descriptionText }}
        </label>
        <quill-editor 
          v-model="form.description" 
          :placeholder="$t('form-create-item.description-placeholder')" 
        />
      </b-form-group>

      <b-col cols="12" md="12" class="d-flex">
        <b-form-group :label="$t('form-create-item.banner-image')" label-for="banner">
          <file-upload
            v-model="imageForum"
            type="image"
            cancelable
            :placeholder="$t('form-create-item.image')"
            :drop-placeholder="$t('form-create-item.drop-placeholder')"
            @cancel="imageSrc = null"
          />
        </b-form-group>
        <!-- Send notification -->
        <b-form-group v-if="canSendNotification" class="pl-2" style="margin-top: 8px;">
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-model="sendNotification"
              class="mr-2"
              @change="onSendNotificationChange"
            />
            <span class="text-dark font-weight-normal notification-label">
              {{ $t("posts.post-creation.action.email-notification") }} (Staff)
            </span>
          </div>
        </b-form-group>
      </b-col>

      <b-col v-if="canSendNotification && sendNotification" cols="12">
        <b-form-group :label="$t('form-create-item.email-subject-label')" label-for="forum-email-subject">
          <b-form-input
            id="forum-email-subject"
            v-model="emailSubject"
            :placeholder="$t('form-create-item.email-subject-placeholder')"
            maxlength="200"
            :disabled="isSubmitting"
          />
          <small class="text-muted d-block mt-1">
            {{ $t('form-create-item.email-subject-default-note', { defaultSubject: defaultEmailSubject }) }}
          </small>
        </b-form-group>
      </b-col>
    </b-form>
  </b-modal>
</template>

<script>
import ToastificationContentVue from '@/@core/components/toastification/ToastificationContent.vue';
import { quillEditor } from 'vue-quill-editor';
import ForumPlaceholder from '@/assets/images/placeholders/light/forums.svg';
import FileUpload from '@core/components/files/FileUpload.vue';
import LoadingModal from '@core/components/modal/LoadingModal.vue';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';


export default {
  name: 'ForumCreateEditModal',
  components: {
    quillEditor,
    FileUpload,
    LoadingModal,
  },
  props: {
    forum: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {},
      imageForum: null,
      imageSrc: null,
      sendNotification: false,
      emailSubject: '',
      isSubmitting: false
    };
  },
  computed: {
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    placeholder() {
      return ForumPlaceholder;
    },
    collective() {
      return this.$store.state.collective.collective;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    canSendNotification() {
      return this.isStaff;
    },
    isNexos() {
      return process.env.VUE_APP_CUSTOMER === 'nexos';
    },
    showCreateTitle() {
      return !this.isNexos || this.isStaff;
    },
    descriptionText() {
      return this.isNexos ? this.$t('form-create-item.description2') : this.$t('form-create-item.description');
    },
    broadcastCommunityName() {
      const name = this.collective?.name;
      if (!name) {
        return this.collective?.title || '';
      }
      return translateTranslationTable(this.currentLocale, name) || this.collective?.title || '';
    },
    defaultEmailSubject() {
      if (this.isNexos) {
        return this.$t('form-create-item.email-subject-default-nexos');
      }
      return this.$t('form-create-item.email-subject-default-generic', {
        communityName: this.broadcastCommunityName,
      });
    },
  },
  watch: {
    forum: {
      immediate: true,
      handler(newForum) {
        if (!newForum) return;

        const locale = this.$store?.state?.locale?.currentLocale;
        const getTranslated = (field) => {
          if (!field) return '';
          if (typeof field === 'string') return field;
          if (locale && field[locale]) return field[locale];
          const values = typeof field === 'object' ? Object.values(field) : [];
          return values && values[0] ? values[0] : '';
        };

        this.form = {
          ...this.form,
          name: getTranslated(newForum.name),
          description: getTranslated(newForum.description),
        };
      },
    },
  },
  // Fetch data when component is created!
  async created() {

  },
  methods: {
    normalizeEmailSubject(value) {
      if (typeof value !== 'string') {
        return '';
      }
      return value.trim().replace(/\s+/g, ' ').slice(0, 200);
    },
    onSendNotificationChange(checked) {
      if (!checked) {
        this.emailSubject = '';
      }
    },
    base64Encode(data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    async handleCreateForum(bvModalEvt) {
      bvModalEvt.preventDefault();

      if (this.isSubmitting) {
        return;
      }

      if (!this.form.name && !this.form.description) {
        this.$toast({
          component: ToastificationContentVue,
          props: {
            title: this.$t('error-message.general-error'),
            message: this.$t('form-create-item.title-and-description-required'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
        bvModalEvt.preventDefault();
        return;
      }
      
      if (!this.form.name && !this.showCreateTitle) {
        this.form.name = '';
      }
      this.isSubmitting = true;
      try {
        const requestConfig = {
          name: {},
          description: {},
          notifyAllMembers: this.sendNotification,
        };
        const locale = this.$store.state.locale.currentLocale;
        requestConfig.name[locale] = this.form.name;
        requestConfig.description[locale] = this.form.description;
        if (this.sendNotification) {
          const trimmedEmailSubject = this.normalizeEmailSubject(this.emailSubject);
          if (trimmedEmailSubject) {
            requestConfig.emailSubject = trimmedEmailSubject;
          }
        }
        
        const response = await this.$store.dispatch('createItem', {
          item: {
            itemType: 'forums',
            storedKey: 'list',
            requestConfig,
          },
          file: this.imageForum,
        });
        
        const forumData = response && response.data ? response.data : null;
        const isPendingModeration = forumData && (forumData.moderationStatus === 0 ||
          (forumData.moderationStatus === null && !this.isStaff));

        if (isPendingModeration) {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: this.$t('success-message.created-pending-review'),
              icon: 'ClockIcon',
              variant: 'warning',
            },
          });
        } else {
          this.$toast({
            component: ToastificationContentVue,
            props: {
              title: this.$t('success-message.general-success-create'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        }

        this.$nextTick(() => {
          this.$bvModal.hide('modal-create-forums');
        });
        
        this.resetForm();
      } catch {
        this.$toast({
          component: ToastificationContentVue,
          props: {
            title: this.$t('error-message.general-error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.form = {};
      this.imageForum = null;
      this.sendNotification = false;
      this.emailSubject = '';
    },
  },
};
</script>

<style scoped>
/* Improve checkbox and label alignment */
.form-group .d-flex {
  gap: 0.5rem;
}

/* Ensure proper spacing for the notification checkbox */
.form-group .form-checkbox {
  margin-top: 0.25rem;
}

/* Better text styling for the notification label */
.notification-label {
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
}
</style>
